import { useState } from "react";
import { Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useTeamStore } from "@/stores/team-store";
import { useCreateTeamMutation } from "@/lib/tanstack-query/teams";
import { CreateInstitutionModal } from "../institution/CreateInstitutionModal";
import { SelectInstitution } from "../institution/SelectInstitution";
import type { TeamSummaryDto } from "@/lib/tanstack-query/dtos/team.dto";

interface CreateTeamModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onTeamCreate: (team: TeamSummaryDto) => void;
}

export const CreateTeamModal = ({
  open,
  onOpenChange,
  onTeamCreate,
}: CreateTeamModalProps) => {
  const { setCurrentTeam } = useTeamStore();
  const createTeamMutation = useCreateTeamMutation();

  const [name, setName] = useState("");
  const [institutionModal, setInstitutionModal] = useState(false);
  const [institution, setInstitution] = useState<string | null>(null);

  const handleSubmit = async (event: React.SubmitEvent) => {
    event.preventDefault();

    // Validation
    const trimmedName = name.trim();
    if (!trimmedName) {
      toast.error("Team name is required");
      return;
    }

    try {
      const newTeam = await createTeamMutation.mutateAsync({
        name: trimmedName,
        institutionId: institution,
      });

      // Set the newly created team as current
      setCurrentTeam(newTeam);

      toast.success(`Successfully created ${trimmedName}!`);

      // Reset form and close modal
      setName("");
      onOpenChange(false);
      onTeamCreate(newTeam);
    } catch (error) {
      toast.error(
        // @ts-expect-error todo: fix later
        error.response?.data?.message || "Failed to create team",
      );
    }
  };

  const handleOpenChange = (newOpen: boolean) => {
    if (!createTeamMutation.isPending) {
      if (!newOpen) {
        setName("");
      }
      onOpenChange(newOpen);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/20 rounded-lg">
                <Building2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <DialogTitle>Create Team</DialogTitle>
                <DialogDescription>
                  Give your team a name. You can invite team members after it's
                  created.
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="space-y-2">
              <Label htmlFor="name" className="text-sm font-medium">
                Team Name <span className="text-red-500">*</span>
              </Label>
              <Input
                id="name"
                name="name"
                type="text"
                placeholder="e.g., MIT Team"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                disabled={createTeamMutation.isPending}
              />
            </div>

            <SelectInstitution
              value={institution}
              onValueChange={setInstitution}
              disabled={createTeamMutation.isPending}
            />

            <DialogFooter className="border-t pt-6">
              <Button
                type="button"
                variant="outline"
                onClick={() => handleOpenChange(false)}
                disabled={createTeamMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createTeamMutation.isPending}
                className="min-w-[120px]"
              >
                {createTeamMutation.isPending ? (
                  <>
                    <span className="mr-2">Creating...</span>
                    <div className="h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  </>
                ) : (
                  "Create Team"
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <CreateInstitutionModal
        open={institutionModal}
        onOpenChange={setInstitutionModal}
        setInstitution={(i) => setInstitution(i.id)}
      />
    </>
  );
};
